import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'api-service';
import { contentAdapter } from 'store/content/adapters';
import { getContentPayload } from 'store/content/contentHelpers';
import { setExcelFilters } from 'store/excelExport/excelExportSlice';
import {
  getRequestTimeDuration,
  getTimestamp,
  magicNumbers,
  mapApiPlatformToAdapted,
  mapPlatform,
} from 'utils';

export const setCopyNewsExcelFilters = createAsyncThunk(
  'contentDetailReducer/setCopyNewsExcelFilters',
  async (postId, thunkAPI) => {
    const { q, query, source, ...filterParams } = getContentPayload(
      'news-copy',
      thunkAPI
    );

    const params = {
      ...filterParams,
      api_path: '/news/{id}/copies',
      id: postId,
      sort: 'time',
      sort_type: 'asc',
    };

    thunkAPI.dispatch(
      setExcelFilters({
        params,
        exportPage: 'content',
        exportPlatform: 'newsCopy',
      })
    );
  }
);

export const defaultPlatformParams = (platform, thunkAPI) => {
  const wantTagContent =
    thunkAPI.getState().settingsReducer.filters.public.wantTagContent
      ?.selectedValue;

  let defaultParams = {
    want_tag_content: wantTagContent ? 1 : 0,
  };

  switch (platform) {
    case 'news-copy':
      defaultParams = {
        ...defaultParams,
        api_path: '/news/{id}/copies',
      };
      break;
    case 'news':
      defaultParams = {
        ...defaultParams,
        api_path: '/news/posts/{id}',
      };
      break;
    case 'newspaper':
      defaultParams = {
        ...defaultParams,
        api_path: '/newspaper/posts/{id}',
      };
      break;
    case 'telegram':
      defaultParams = {
        ...defaultParams,
        api_path: '/telegram/posts/{id}',
        want_republishers: 1,
      };
      break;
    case 'telegramComment':
      defaultParams = {
        ...defaultParams,
        api_path: '/telegram/comments',
      };
      break;
    case 'instagram':
      defaultParams = {
        ...defaultParams,
        api_path: '/instagram/posts/{id}',
      };
      break;
    case 'instagramStory':
      defaultParams = {
        ...defaultParams,
        api_path: '/instagram/stories',
      };
      break;
    case 'instagramComment':
      defaultParams = {
        ...defaultParams,
        api_path: '/instagram/comments',
      };
      break;
    case 'twitter':
      defaultParams = {
        ...defaultParams,
        api_path: '/twitter/posts/{id}',
      };
      break;
    case 'twitter-retweets':
      defaultParams = {
        ...defaultParams,
        api_path: '/twitter/posts/{id}/retweets',
      };
      break;
    case 'virasty':
      defaultParams = {
        ...defaultParams,
        api_path: '/virasty/posts/{id}',
      };
      break;
    case 'virasty-reposts':
      defaultParams = {
        ...defaultParams,
        api_path: '/virasty/posts/{id}/copies',
      };
      break;
    case 'nativePlatforms':
      defaultParams = {
        ...defaultParams,
        api_path: '/iranian-platforms/posts/{id}',
      };
      break;
    case 'linkedin':
      defaultParams = {
        ...defaultParams,
        api_path: '/linkedin/posts/{id}',
      };
  }
  return defaultParams;
};

/**
 * Instagram comments: from post publish time to now, capped at 1 year.
 * Falls back to the last year when publish time is missing or the post API fails.
 */
const getInstagramCommentsTimeRange = async (postId, thunkAPI) => {
  const nowInSeconds = Math.floor(
    Date.now() / magicNumbers.CONVERT_TIMESTAMP_TO_DATE_FACTOR
  );
  const oneYearAgo =
    nowInSeconds - getRequestTimeDuration({ timeDuration: '12m' });
  const oneYearRange = {
    from: oneYearAgo,
    to: nowInSeconds,
  };

  try {
    const postResponse = await apiGet('/search/api/v1/search', {
      ...defaultPlatformParams('instagram', thunkAPI),
      id: postId,
    });
    const postTime = postResponse?.data?.result?.data?.time;

    if (postTime != null) {
      return {
        from: Math.max(getTimestamp(postTime), oneYearAgo),
        to: nowInSeconds,
      };
    }
  } catch {
    return oneYearRange;
  }

  return oneYearRange;
};

export const fetchContentDetail = createAsyncThunk(
  'contentDetailReducer/fetchContentDetail',
  async (payload, thunkAPI) => {
    const { platform, params } = payload;

    const defaultParams = defaultPlatformParams(platform, thunkAPI);

    try {
      const response = await apiGet('/search/api/v1/search', {
        ...defaultParams,
        ...params,
      });

      const content = response?.data?.result?.data;

      return contentAdapter(
        content,
        mapApiPlatformToAdapted(content?.platform) || platform
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchOriginalNewsContent = createAsyncThunk(
  'contentDetailReducer/fetchOriginalNewsContent',
  async (payload, thunkAPI) => {
    const { id } = payload;

    try {
      const response = await apiGet('/search/api/v1/search', {
        ...defaultPlatformParams('news', thunkAPI),
        id,
      });

      const content = response?.data?.result?.data;

      return contentAdapter(
        content,
        mapApiPlatformToAdapted(content?.platform) || 'news'
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchNote = createAsyncThunk(
  'contentDetailReducer/fetchNote',
  async (params, thunkAPI) => {
    const { platform, postId } = params;

    try {
      const response = await apiGet(
        `/search/api/v2/note/${platform}/${postId}`
      );

      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchCommentsOrCopyContent = createAsyncThunk(
  'contentDetailReducer/fetchCommentsOrCopyContent',
  async (payload, thunkAPI) => {
    const { platform, params } = payload;

    const defaultParams = defaultPlatformParams(platform, thunkAPI);
    let requestParams = { ...defaultParams, ...params };

    if (platform === 'news-copy') {
      const { contentReducer } = thunkAPI.getState();
      const keywordFilter = contentReducer.resultSearchQuery['news-copy'];
      const { q, query, ...filterParams } = getContentPayload(
        'news-copy',
        thunkAPI
      );

      requestParams = {
        ...filterParams,
        ...requestParams,
        keyword_filter: keywordFilter || undefined,
        sort: 'time',
        sort_type: 'asc',
      };

      thunkAPI.dispatch(setCopyNewsExcelFilters(params.id));
    }

    try {
      if (platform === 'instagramComment') {
        const timeRange = await getInstagramCommentsTimeRange(
          params.post_ids?.[0],
          thunkAPI
        );
        requestParams = {
          ...requestParams,
          ...timeRange,
        };
      }

      const response = await apiGet('/search/api/v1/search', requestParams);
      return {
        data: response?.data?.result?.data,
        total: response?.data?.result?.meta?.total,
        page: params?.page ?? 1,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updatePost = createAsyncThunk(
  'contentDetailReducer/updatePost',
  async (params = {}, thunkAPI) => {
    try {
      const response = await apiGet(
        `/search/api/${
          params.platform === 'twitter_post' ||
          params.platform === 'instagram_post'
            ? 'v3'
            : 'v2'
        }/god/`,
        params
      );
      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addNote = createAsyncThunk(
  'contentDetailReducer/addNote',
  async (params = {}, thunkAPI) => {
    try {
      const response = await apiPost('/search/api/v2/note', params);
      return response?.data?.result?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchAIAssistant = createAsyncThunk(
  'contentDetailReducer/fetchAIAssistant',
  async (params, thunkAPI) => {
    const { postId, platform } = params;
    try {
      const response = await apiGet('/search/api/v1/aia/chat', {
        question_id: 1,
        post_id: postId,
        platform: mapPlatform[platform]?.apiPath,
      });
      return response?.data?.result?.data?.[0];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const contentDetailInitialState = {
  dontShowMainPostCopyContent: true,
  isQuote: false,
  page: 1,
  content: {
    data: [],
    loading: false,
    error: undefined,
  },
  originalContent: {
    data: null,
    loading: false,
    error: undefined,
  },
  update: {
    loading: false,
    error: undefined,
  },
  commentsOrCopyContent: {
    data: [],
    loading: false,
    error: undefined,
    total: 0,
  },
  addNewNote: {
    loading: false,
    error: undefined,
  },
  note: {
    data: null,
    loading: false,
    error: undefined,
  },
};

export const contentDetailSlice = createSlice({
  name: 'contentDetailSlice',
  initialState: contentDetailInitialState,
  reducers: {
    changeContentDetailData: (state, action) => {
      state.content.data = {
        ...state.content.data,
        ...action?.payload,
      };
    },
    clearContent: (state) => {
      state.content.data = [];
      state.content.loading = false;
      state.content.error = undefined;
    },
    changeContentDetailValue: (state, action) => {
      const { key, value } = action?.payload;
      state[key] = value;

      if (key === 'isQuote' || key === 'dontShowMainPostCopyContent') {
        state.page = 1;
        state.commentsOrCopyContent.data = [];
        state.commentsOrCopyContent.loading = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNote.fulfilled, (state, action) => {
        state.note.data = action.payload;
        state.note.loading = false;
        state.note.error = null;
      })
      .addCase(fetchNote.pending, (state) => {
        state.note.data = null;
        state.note.loading = true;
        state.note.error = null;
      })
      .addCase(fetchNote.rejected, (state, action) => {
        state.note.data = null;
        // از بک اند اگر یادداشت نبود خطای 404 برمیگرده که مجبوریم اینجوری به عنوان خطا ثبت نکنیم که مقدار خالی در نظر بگیره
        if (action.payload.response.status !== 404) {
          state.note.error = action.payload;
        }
        state.note.loading = false;
      })
      .addCase(fetchContentDetail.fulfilled, (state, action) => {
        state.content.data = action.payload;
        // desc: برای زمانی که بین تب جزئیات مطلب و تب نظرات یا بازنشرها جا به جا میشیم
        state.page = 1;
        state.content.loading = false;
        state.content.error = null;
      })
      .addCase(fetchContentDetail.pending, (state) => {
        state.content.loading = true;
        state.content.error = null;
      })
      .addCase(fetchContentDetail.rejected, (state, action) => {
        state.content.loading = false;
        state.content.error = action.payload;
      })
      .addCase(fetchOriginalNewsContent.fulfilled, (state, action) => {
        state.originalContent.data = action.payload;
        state.originalContent.loading = false;
        state.originalContent.error = null;
      })
      .addCase(fetchOriginalNewsContent.pending, (state) => {
        state.originalContent.loading = true;
        state.originalContent.error = null;
      })
      .addCase(fetchOriginalNewsContent.rejected, (state, action) => {
        state.originalContent.loading = false;
        state.originalContent.error = action.payload;
      })
      .addCase(fetchCommentsOrCopyContent.fulfilled, (state, action) => {
        const { data = [], total = 0, page = 1 } = action.payload;
        state.commentsOrCopyContent.data =
          page == 1 ? data : [...state.commentsOrCopyContent.data, ...data];
        state.commentsOrCopyContent.total = total;
        state.commentsOrCopyContent.loading = false;
        state.commentsOrCopyContent.error = null;
      })
      .addCase(fetchCommentsOrCopyContent.pending, (state, action) => {
        const { page = 1 } = action.meta.arg.params;
        state.commentsOrCopyContent.loading = true;
        state.commentsOrCopyContent.error = null;
        if (page === 1) {
          state.commentsOrCopyContent.data = [];
        }
      })
      .addCase(fetchCommentsOrCopyContent.rejected, (state, action) => {
        state.commentsOrCopyContent.loading = false;
        state.commentsOrCopyContent.error = action.payload;
      })
      .addCase(updatePost.fulfilled, (state) => {
        state.update.loading = false;
        state.update.error = null;
      })
      .addCase(updatePost.pending, (state) => {
        state.update.loading = true;
        state.update.error = null;
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.update.loading = false;
        state.update.error = action.payload;
      })
      .addCase(addNote.fulfilled, (state) => {
        state.addNewNote.loading = false;
        state.addNewNote.error = null;
      })
      .addCase(addNote.pending, (state) => {
        state.addNewNote.loading = true;
        state.addNewNote.error = null;
      })
      .addCase(addNote.rejected, (state, action) => {
        state.addNewNote.loading = false;
        state.addNewNote.error = action.payload;
      });
  },
});

export const {
  changeContentDetailData,
  changeContentDetailValue,
  clearContent,
} = contentDetailSlice.actions;
